import {
  AIHighlight,
  CharacterCount,
  CodeBlockLowlight,
  Color,
  CustomKeymap,
  GalleryExtension,
  GlobalDragHandle,
  HighlightExtension,
  HorizontalRule,
  ImageExtension,
  LinkCardExtension,
  LinkExtension,
  MarkdownExtension,
  Mathematics,
  Placeholder,
  StarterKit,
  TableKit,
  TaskItem,
  TaskList,
  TextStyle,
  TiptapImage,
  TiptapUnderline,
  Twitter,
  Youtube,
} from '@/components/tiptap-editor/extensions'
import { UploadImagesPlugin } from '@/components/tiptap-editor/plugins'
import { onUpload, onValidate } from '@/lib/image/uploader'
import { cx } from 'class-variance-authority'
import { common, createLowlight } from 'lowlight'

// TODO I am using cx here to get tailwind autocomplete working, idk if someone else can write a regex to just capture the class key in objects
const aiHighlight = AIHighlight

// You can overwrite the placeholder with your own configuration
const placeholder = Placeholder

// Custom Link extension - user controls target via "Open in new window" checkbox
// Internal links (default): open in same window
// External links (checkbox checked): open in new window with rel="noopener noreferrer nofollow"
const linkExtension = LinkExtension.configure({
  openOnClick: false,
})

// Note: Upload plugin is now handled by ImageExtension
// Keep tiptapImage config for reference but don't use it in extensions array
const tiptapImage = TiptapImage.extend({
  addProseMirrorPlugins() {
    return [
      UploadImagesPlugin({
        imageClass: cx('opacity-40 rounded-lg border border-stone-200'),
      }),
    ]
  },
}).configure({
  allowBase64: true,
  HTMLAttributes: {
    class: cx('rounded-lg border border-muted'),
  },
})

// Use component-based ImageExtension instead of UpdatedImage
const imageExtension = ImageExtension.configure({
  onUpload: onUpload,
  validateFn: onValidate,
  imageClass: cx('rounded-lg border border-muted'),
})

const taskList = TaskList.configure({
  HTMLAttributes: {
    class: cx('not-prose pl-2 '),
  },
})

const taskItem = TaskItem.configure({
  HTMLAttributes: {
    class: cx('flex gap-2 items-start my-4'),
  },
  nested: true,
})

// Tables are wrapped in a div.tableWrapper (also in the saved HTML) so wide
// tables scroll horizontally instead of overflowing the article column.
const tableKit = TableKit.configure({
  table: {
    resizable: true,
    renderWrapper: true,
    allowTableNodeSelection: true,
  },
})

const horizontalRule = HorizontalRule.configure({
  HTMLAttributes: {
    class: cx('mt-4 mb-6 border-t border-muted-foreground'),
  },
})

const starterKit = StarterKit.configure({
  link: false, // Disable default link extension - we use custom LinkExtension
  bulletList: {
    HTMLAttributes: {
      class: cx('list-disc list-outside leading-3 -mt-2'),
    },
  },
  orderedList: {
    HTMLAttributes: {
      class: cx('list-decimal list-outside leading-3 -mt-2'),
    },
  },
  listItem: {
    HTMLAttributes: {
      class: cx('leading-normal -mb-2'),
    },
  },
  blockquote: {
    HTMLAttributes: {
      class: cx('border-l-4 border-primary'),
    },
  },
  codeBlock: {
    HTMLAttributes: {
      class: cx(
        'rounded-md bg-muted text-muted-foreground border p-5 font-mono font-medium',
      ),
    },
  },
  code: {
    HTMLAttributes: {
      class: cx('rounded-md bg-muted  px-1.5 py-1 font-mono font-medium'),
      spellcheck: 'false',
    },
  },
  horizontalRule: false,
  dropcursor: {
    color: '#DBEAFE',
    width: 4,
  },
  gapcursor: false,
})

const codeBlockLowlight = CodeBlockLowlight.configure({
  // configure lowlight: common /  all / use highlightJS in case there is a need to specify certain language grammars only
  // common: covers 37 language grammars which should be good enough in most cases
  lowlight: createLowlight(common),
})

const youtube = Youtube.configure({
  HTMLAttributes: {
    class: cx('rounded-lg border border-muted'),
  },
  inline: false,
})

const twitter = Twitter.configure({
  HTMLAttributes: {
    class: cx('not-prose'),
  },
  inline: false,
})

const mathematics = Mathematics.configure({
  HTMLAttributes: {
    class: cx('text-foreground rounded p-1 hover:bg-accent cursor-pointer'),
  },
  katexOptions: {
    throwOnError: false,
  },
})

const characterCount = CharacterCount.configure()

// Gallery extension for multiple images (up to 6)
const galleryExtension = GalleryExtension.configure({
  onUpload: onUpload,
  validateFn: onValidate,
})

const markdownExtension = MarkdownExtension.configure({
  html: true,
  tightLists: true,
  tightListClass: 'tight',
  bulletListMarker: '-',
  linkify: false,
  breaks: false,
  transformPastedText: false,
  transformCopiedText: false,
})

export const defaultExtensions = [
  starterKit,
  placeholder,
  linkExtension, // Custom link extension with user-controlled target
  taskList,
  taskItem,
  tableKit,

  // tiptapImage, // Removed - using imageExtension instead
  imageExtension, // Use component-based ImageExtension (handles uploads via addProseMirrorPlugins)
  // updatedImage, // Keep commented for backward compatibility, can remove later
  horizontalRule,
  aiHighlight,
  codeBlockLowlight,
  youtube,
  twitter,
  mathematics,
  characterCount,
  TiptapUnderline,
  markdownExtension,
  HighlightExtension,
  TextStyle,
  Color,
  CustomKeymap,
  GlobalDragHandle,
  LinkCardExtension,
  galleryExtension,
]
